import { useCallback, useMemo } from "react"
import {
  Block,
  ChevronRight,
  Divider,
  LabelMedium,
  ListItem,
  ListItemLabel,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

export type HomepageExploreSectionPostProps = {
  image: string
  title: string
  content: string
  href: string
}

type PostImageProps = Pick<HomepageExploreSectionPostProps, "image" | "title">

// FIXME: List item accepts these props as functions, probably need
// to be handled better on Clay side
function ChevronIcon({ href }: Pick<HomepageExploreSectionPostProps, "href">) {
  // We need a slug for blog post for Posthog usage
  const postSlug = href.split("/").at(-2)

  return (
    <Block
      data-attr={`blog-${postSlug}`}
      as="a"
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      <ChevronRight color="contentStateDisabled" />
    </Block>
  )
}

function PostImage({ title, image }: PostImageProps) {
  const [css] = useStyletron()

  return (
    <Block
      height="48px"
      width="48px"
      overflow="hidden"
      className={css({ borderRadius: "2px" })}
    >
      <Block
        as="img"
        src={image}
        alt={title}
        className={css({ objectFit: "cover", height: "100%", width: "100%" })}
      />
    </Block>
  )
}

export default function HomepageExploreSectionPost({
  image,
  title,
  content,
  href,
}: HomepageExploreSectionPostProps) {
  const artworkImage = useCallback(
    () => PostImage({ image, title }),
    [image, title],
  )

  const endEnhancer = useCallback(() => ChevronIcon({ href }), [href])

  const truncatedContent = useMemo(
    () => (content.length > 90 ? `${content.slice(0, 90)}...` : content),
    [content],
  )

  return (
    <>
      <ListItem
        endEnhancer={endEnhancer}
        artwork={artworkImage}
        overrides={{
          ArtworkContainer: {
            style: () => ({ height: "80px", width: "80px" }),
          },
          Content: {
            style: () => ({ paddingTop: "4px", paddingBottom: "4px" }),
          },
          EndEnhancerContainer: {
            style: () => ({ padding: "16px" }),
          },
        }}
      >
        <ListItemLabel
          description={
            <ParagraphSmall color="contentSecondary" as="span">
              {truncatedContent}
            </ParagraphSmall>
          }
        >
          <LabelMedium marginBottom="4px" as="span">
            {title}
          </LabelMedium>
        </ListItemLabel>
      </ListItem>
      <Divider $size="cell" />
    </>
  )
}
